import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, CheckCircle2, Shield, X, Loader2, Wand2, Zap, ChefHat, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_a369e460-91b8-45cf-ab2f-6805bc15e17c/artifacts/riz8gmwr_w13dd3z5_event%20%281%29.png";
const HERO = "https://images.unsplash.com/photo-1769812344096-8993973bd233?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [adminModal, setAdminModal] = useState(false);
  const navigate = useNavigate();

  const handleLogin = () => {
    setLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    "🪄 Magic Event — l'AI genera menù, scaletta e preventivo da una semplice descrizione",
    "Pianificazione evento guidata multi-step",
    "Database di 200+ piatti italiani precaricati",
    "Generatore automatico di menù e preventivi con AI",
    "OCR fatture e DDT per gestione fornitori",
    "Calcolo automatico staff cucina e sala",
  ];

  const magicSteps = [
    { icon: ChefHat, text: "Selezione automatica dei piatti dal database (210 piatti)" },
    { icon: CalendarClock, text: "Scaletta dettagliata con orari, portate e intrattenimento" },
    { icon: Zap, text: "Ricarichi calcolati per stare nel budget del cliente" },
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left – Brand */}
      <div className="flex-1 hero-bg flex items-center justify-center px-8 py-12 lg:py-0">
        <div className="max-w-md w-full">
          <img src={LOGO} alt="Event System 360" className="h-24 mb-10" style={{ filter: "hue-rotate(50deg) saturate(1.2)" }} />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] text-[#1D4ED8] text-xs font-semibold uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Piattaforma per Ristoratori e organizzatori di eventi
          </div>
          <h1 className="font-display text-5xl lg:text-6xl text-[#0F2741] leading-[1.05] tracking-tight">
            L'arte di organizzare<br />
            <span className="text-[#2563EB] italic">eventi indimenticabili.</span>
          </h1>
          <p className="mt-6 text-base text-slate-600 leading-relaxed">
            Dal primo brindisi all'ultimo ballo. Pianifica, gestisci e preventiva
            ogni dettaglio del tuo evento di ristorazione con la precisione di un grande chef.
          </p>

          {/* Magic Event Promo (badge sopra il bottone login) */}
          <button
            type="button"
            onClick={handleLogin}
            disabled={loading}
            data-testid="magic-promo-card"
            className="mt-8 group w-full text-left relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-purple-600 via-fuchsia-500 to-orange-500 text-white shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-all hover:-translate-y-0.5"
          >
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-yellow-300/20 rounded-full blur-2xl" />
            <div className="absolute -left-4 -bottom-4 w-20 h-20 bg-pink-300/30 rounded-full blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur">
                  <Wand2 className="w-4 h-4" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-90">Novità</span>
                <span className="inline-flex w-2 h-2 rounded-full bg-yellow-300 animate-ping" />
              </div>
              <h3 className="font-display text-2xl leading-tight">
                Magic Event<span className="opacity-70 text-base font-ui font-normal"> — l'evento si crea da solo</span>
              </h3>
              <p className="text-sm opacity-90 mt-1.5 leading-relaxed">
                Dimmi tipo di evento, data, ospiti e budget. L'AI ti restituisce <strong>menù completo</strong>, <strong>scaletta dettagliata</strong> con orari e <strong>preventivo già pronto</strong> in 30 secondi.
              </p>
              <ul className="mt-3 space-y-1.5">
                {magicSteps.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <s.icon className="w-3.5 h-3.5 opacity-90" />
                    <span className="opacity-95">{s.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold bg-white/20 backdrop-blur px-3 py-1.5 rounded-lg group-hover:bg-white/30 transition-all">
                Provalo subito (gratis con login)
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          <button
            onClick={handleLogin}
            disabled={loading}
            data-testid="btn-google-login"
            className="mt-6 group w-full lg:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-[#0F2741] hover:bg-[#16385C] text-white rounded-xl font-ui font-semibold transition-all shadow-lg shadow-slate-900/10 hover:shadow-xl hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41 35.3 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Accedi con Google
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="text-xs text-slate-400 mt-4">
            Effettuando l'accesso accetti i Termini di Servizio e la Privacy Policy.
          </p>

          <div className="mt-8 pt-6 border-t border-slate-200/70">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Sei un professionista del settore?</p>
            <p className="text-sm text-slate-600">Fotografo, artista o fornitore food? Inserisci la tua scheda nel nostro database.</p>
            <button
              onClick={() => navigate("/iscriviti")}
              data-testid="btn-pro-register"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] group"
            >
              Iscriviti come professionista
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Right – Showcase */}
      <div className="lg:flex-1 lg:max-w-xl bg-[#0F2741] relative overflow-hidden hidden lg:flex flex-col justify-center px-12 py-16">
        <div className="absolute inset-0 opacity-25">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2741] via-[#0F2741]/95 to-[#2563EB]/40" />
        <div className="relative z-10 max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold mb-4">Il tuo event planner</p>
          <h2 className="font-display text-4xl text-white leading-tight">
            "Un evento perfetto è una sinfonia di dettagli."
          </h2>
          <ul className="mt-10 space-y-4">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-white/90 text-sm">
                <CheckCircle2 className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Floating SA button - bottom right of login page */}
      <button
        onClick={() => setAdminModal(true)}
        data-testid="btn-sa-login"
        title="Accesso Super Admin"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white font-bold text-lg shadow-2xl hover:scale-110 transition-all flex items-center justify-center ring-4 ring-amber-200/50"
      >
        <Shield className="w-5 h-5 absolute opacity-30" />
        <span className="relative tracking-tight">SA</span>
      </button>

      {adminModal && <AdminLoginModal onClose={() => setAdminModal(false)} />}
    </div>
  );
}

function AdminLoginModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/auth/admin-login", { email: email.trim(), password });
      if (r.data?.session_token) {
        localStorage.setItem("session_token", r.data.session_token);
      }
      toast.success("Accesso Super Admin riuscito");
      // Full reload to refresh AuthContext from cookie
      window.location.href = "/admin";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accesso negato");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-sm space-y-4 shadow-2xl" data-testid="admin-login-modal">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-white shadow-md">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Riservato</p>
              <h3 className="font-display text-xl text-[#0F2741] leading-tight">Accesso Super Admin</h3>
            </div>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-xs text-slate-500">L'accesso è riservato agli amministratori autorizzati della piattaforma.</p>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email amministratore</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="input-sa-email"
            autoComplete="username"
            placeholder="admin@example.com"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="input-sa-password"
            autoComplete="current-password"
            placeholder="••••••••"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !email || !password}
          data-testid="btn-sa-submit"
          className="w-full mt-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} Accedi al pannello
        </button>
      </form>
    </div>
  );
}
